import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2Icon } from "lucide-react";
import type { Control, FieldPath, FieldValues } from "react-hook-form";

import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "./form";

export interface FormSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FormSelectOptionGroup {
  label: string;
  options: FormSelectOption[];
}

export interface FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  control: Control<TFieldValues>;
  name: TName;
  label: string;
  placeholder?: string;
  /** Options can be flat or grouped */
  options: FormSelectOption[] | FormSelectOptionGroup[];
  disabled?: boolean;
  className?: string;
  description?: string;
  /** Marks field as required (shows asterisk) */
  required?: boolean;
  /** Shows a loading spinner in the select trigger */
  loading?: boolean;
  /** Text to display when no options are available */
  emptyText?: string;
  /** Accessible label for screen readers */
  "aria-label"?: string;
}

/**
 * Form-connected select dropdown field with enhanced features
 *
 * @description Standard dropdown select with support for loading states,
 * empty states, and grouped options. Mobile-first responsive design.
 *
 * @example Basic usage
 * ```tsx
 * <FormSelect
 *   control={form.control}
 *   name="country"
 *   label="Country"
 *   placeholder="Select a country"
 *   options={[
 *     { value: "us", label: "United States" },
 *     { value: "ca", label: "Canada" },
 *     { value: "uk", label: "United Kingdom" },
 *   ]}
 * />
 * ```
 *
 * @example With grouped options
 * ```tsx
 * <FormSelect
 *   control={form.control}
 *   name="timezone"
 *   label="Timezone"
 *   options={[
 *     {
 *       label: "Americas",
 *       options: [
 *         { value: "america/new_york", label: "Eastern Time" },
 *         { value: "america/los_angeles", label: "Pacific Time" },
 *       ],
 *     },
 *     {
 *       label: "Europe",
 *       options: [
 *         { value: "europe/london", label: "London" },
 *         { value: "europe/paris", label: "Paris" },
 *       ],
 *     },
 *   ]}
 * />
 * ```
 *
 * @example With loading state
 * ```tsx
 * <FormSelect
 *   control={form.control}
 *   name="category"
 *   label="Category"
 *   loading={isLoading}
 *   options={categories ?? []}
 * />
 * ```
 */
export function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  label,
  placeholder,
  options,
  disabled = false,
  className,
  description,
  required,
  loading,
  emptyText = "No options available",
  "aria-label": ariaLabel,
}: FormSelectProps<TFieldValues, TName>) {
  const firstOption = options[0];
  const isGrouped = firstOption !== undefined && isOptionGroup(firstOption);
  const flatOptions = isGrouped
    ? (options as FormSelectOptionGroup[]).flatMap((group) => group.options)
    : (options as FormSelectOption[]);
  const isEmpty = flatOptions.length === 0;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => (
        <FormItem className={className}>
          <FormLabel required={required}>{label}</FormLabel>
          <Select
            value={field.value as string}
            onValueChange={field.onChange}
            disabled={disabled || loading || isEmpty}
          >
            <FormControl>
              <SelectTrigger aria-label={ariaLabel} className="w-full">
                <div className="flex items-center gap-2">
                  {loading && <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <SelectValue placeholder={loading ? "Loading..." : isEmpty ? emptyText : placeholder} />
                </div>
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              {isEmpty ? (
                <div className="py-6 text-center text-sm text-muted-foreground">{emptyText}</div>
              ) : isGrouped ? (
                (options as FormSelectOptionGroup[]).map((group) => (
                  <SelectGroup key={group.label}>
                    <SelectLabel className={cn("px-2 py-1.5 text-xs font-semibold text-muted-foreground")}>
                      {group.label}
                    </SelectLabel>
                    {group.options.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))
              ) : (
                (options as FormSelectOption[]).map((opt) => (
                  <SelectItem key={opt.value} value={opt.value} disabled={opt.disabled}>
                    {opt.label}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          {description && <FormDescription>{description}</FormDescription>}
          <FormMessage />
        </FormItem>
      )}
    />
  );
}

// ============================================================================
// Private utilities
// ============================================================================

/** Type guard to check if an option is a group */
function isOptionGroup(option: FormSelectOption | FormSelectOptionGroup): option is FormSelectOptionGroup {
  return "options" in option && Array.isArray(option.options);
}

